import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { useApp } from '../context/AppContext';

export const OMAMandate = () => {
  const { data } = useApp();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMinistry, setSelectedMinistry] = useState('all');

  const mandateAreas = [
    {
      id: 1,
      category: 'Policy Coordination',
      title: 'Cabinet Decisions Implementation',
      description: 'Monitor and ensure timely implementation of all Cabinet decisions across OMAs',
      legalBasis: 'Cabinet Manual (2015), Section 4.2',
      reportingFrequency: 'Monthly',
      lastReported: '2025-01-15',
      status: 'On Track',
      compliance: 87,
      responsibleOMAs: ['All Ministries'],
      kpis: [
        { metric: 'Decisions Implemented', target: data?.cabinetStats?.totalDecisions ?? 45, actual: data?.cabinetStats?.completedDecisions ?? 39, unit: 'decisions' },
        { metric: 'Implementation Rate', target: 100, actual: data?.cabinetStats?.avgImplementationRate ?? 87, unit: '%' },
        { metric: 'Overdue Actions', target: 0, actual: data?.cabinetStats?.overdueDirectives ?? 6, unit: 'items' }
      ],
      recentActivity: 'Last Cabinet decision tracking report submitted 15 Jan 2025',
      escalations: 2
    },
    {
      id: 2,
      category: 'Policy Coordination',
      title: 'National Development Plan (NDP6) Oversight',
      description: 'Coordinate and monitor NDP6 implementation across all sectors and pillars',
      legalBasis: 'NDP6 IMEP Framework (2024)',
      reportingFrequency: 'Quarterly',
      lastReported: '2024-12-20',
      status: 'At Risk',
      compliance: 62,
      responsibleOMAs: ['All Ministries', 'National Planning Commission'],
      kpis: [
        { metric: 'Pillar Progress (Avg)', target: 75, actual: 62, unit: '%' },
        { metric: 'Quarterly Reports Submitted', target: 23, actual: 18, unit: 'reports' },
        { metric: 'Budget Execution', target: 70, actual: 58, unit: '%' }
      ],
      recentActivity: 'Q4 2024 consolidation report overdue from 5 ministries',
      escalations: 5
    },
    {
      id: 3,
      category: 'Procurement Oversight',
      title: 'Public Procurement Compliance',
      description: 'Ensure all OMAs comply with Public Procurement Act and Regulations',
      legalBasis: 'Public Procurement Act (2015), Regulations (2017)',
      reportingFrequency: 'Continuous',
      lastReported: '2025-01-20',
      status: 'Delayed',
      compliance: 45,
      responsibleOMAs: ['All Ministries', 'Public Procurement Unit'],
      kpis: [
        { metric: 'Annual Plans Submitted', target: 23, actual: 11, unit: 'plans' },
        { metric: 'Award Publication (7 days)', target: 100, actual: 64, unit: '%' },
        { metric: 'Non-Compliance Notices', target: 0, actual: 12, unit: 'notices' }
      ],
      recentActivity: 'Critical non-compliance from Ministry of Works & Transport',
      escalations: 8
    },
    {
      id: 4,
      category: 'Performance Management',
      title: 'Ministerial Performance Agreements',
      description: 'Monitor execution of performance agreements signed by Ministers and Permanent Secretaries',
      legalBasis: 'Public Service Act (1995), Performance Management Framework',
      reportingFrequency: 'Quarterly',
      lastReported: '2024-12-31',
      status: 'On Track',
      compliance: 78,
      responsibleOMAs: ['All Ministries'],
      kpis: [
        { metric: 'KPIs Achieved', target: 120, actual: 94, unit: 'KPIs' },
        { metric: 'Performance Score (Avg)', target: 80, actual: 78, unit: '%' },
        { metric: 'Quarterly Reviews Completed', target: 23, actual: 21, unit: 'reviews' }
      ],
      recentActivity: 'Q4 2024 performance reviews completed for 21/23 ministries',
      escalations: 2
    },
    {
      id: 5,
      category: 'Financial Oversight',
      title: 'Budget Execution Monitoring',
      description: 'Track budget allocation, disbursement and expenditure across all votes',
      legalBasis: 'State Finance Act (1991), Treasury Instructions',
      reportingFrequency: 'Monthly',
      lastReported: '2025-01-10',
      status: 'On Track',
      compliance: 91,
      responsibleOMAs: ['All Ministries', 'Ministry of Finance'],
      kpis: [
        { metric: 'Budget Execution Rate', target: 65, actual: 58, unit: '%' },
        { metric: 'Virements Approved', target: 45, actual: 42, unit: 'virements' },
        { metric: 'Underspending Ministries', target: 0, actual: 7, unit: 'ministries' }
      ],
      recentActivity: 'December 2024 expenditure reports consolidated',
      escalations: 1
    },
    {
      id: 6,
      category: 'Service Delivery',
      title: 'Public Service Charter Compliance',
      description: 'Monitor adherence to service delivery standards and citizen charter commitments',
      legalBasis: 'Public Service Charter (2018)',
      reportingFrequency: 'Quarterly',
      lastReported: '2024-12-15',
      status: 'At Risk',
      compliance: 56,
      responsibleOMAs: ['All Ministries', 'Office of the Prime Minister'],
      kpis: [
        { metric: 'Service Standards Met', target: 85, actual: 56, unit: '%' },
        { metric: 'Citizen Complaints Resolved', target: 90, actual: 67, unit: '%' },
        { metric: 'Response Time (Days)', target: 7, actual: 14, unit: 'days' }
      ],
      recentActivity: 'Q4 2024 citizen satisfaction survey results pending',
      escalations: 4
    },
    {
      id: 7,
      category: 'Audit & Compliance',
      title: 'Auditor General Recommendations',
      description: 'Track implementation of Auditor General audit findings and recommendations',
      legalBasis: 'State Finance Act (1991), Audit Act (2005)',
      reportingFrequency: 'Bi-Annual',
      lastReported: '2024-11-30',
      status: 'Delayed',
      compliance: 38,
      responsibleOMAs: ['All Ministries', 'Office of the Auditor General'],
      kpis: [
        { metric: 'Recommendations Implemented', target: 120, actual: 46, unit: 'items' },
        { metric: 'Implementation Rate', target: 80, actual: 38, unit: '%' },
        { metric: 'Outstanding Queries', target: 0, actual: 74, unit: 'queries' }
      ],
      recentActivity: 'FY 2023/24 audit implementation report overdue',
      escalations: 6
    },
    {
      id: 8,
      category: 'Human Resources',
      title: 'Public Service Establishment Control',
      description: 'Monitor staffing levels, vacancies, and HR compliance across public service',
      legalBasis: 'Public Service Act (1995), Establishment Control',
      reportingFrequency: 'Quarterly',
      lastReported: '2025-01-05',
      status: 'On Track',
      compliance: 82,
      responsibleOMAs: ['All Ministries', 'Public Service Commission'],
      kpis: [
        { metric: 'Vacancy Rate', target: 15, actual: 22, unit: '%' },
        { metric: 'Critical Posts Filled', target: 90, actual: 78, unit: '%' },
        { metric: 'Recruitment Compliance', target: 100, actual: 82, unit: '%' }
      ],
      recentActivity: 'Q4 2024 establishment report submitted by all ministries',
      escalations: 0
    },
    {
      id: 9,
      category: 'Anti-Corruption',
      title: 'Anti-Corruption Commission Referrals',
      description: 'Coordinate follow-up on ACC investigations and recommendations involving OMAs',
      legalBasis: 'Anti-Corruption Act (2003)',
      reportingFrequency: 'Continuous',
      lastReported: '2025-01-18',
      status: 'At Risk',
      compliance: 54,
      responsibleOMAs: ['All Ministries', 'Anti-Corruption Commission'],
      kpis: [
        { metric: 'Active Investigations', target: 0, actual: 8, unit: 'cases' },
        { metric: 'Disciplinary Actions Taken', target: 12, actual: 7, unit: 'actions' },
        { metric: 'Prevention Measures Implemented', target: 23, actual: 15, unit: 'measures' }
      ],
      recentActivity: 'New investigation opened against procurement officials',
      escalations: 3
    },
    {
      id: 10,
      category: 'Digital Transformation',
      title: 'e-Government Implementation',
      description: 'Monitor digitalization initiatives and ICT compliance across government',
      legalBasis: 'e-Government Policy (2020), ICT White Paper',
      reportingFrequency: 'Quarterly',
      lastReported: '2024-12-10',
      status: 'On Track',
      compliance: 75,
      responsibleOMAs: ['All Ministries', 'Ministry of ICT'],
      kpis: [
        { metric: 'Digital Services Launched', target: 35, actual: 26, unit: 'services' },
        { metric: 'System Uptime', target: 99, actual: 97, unit: '%' },
        { metric: 'Cybersecurity Compliance', target: 100, actual: 75, unit: '%' }
      ],
      recentActivity: 'Q4 2024 digitalization roadmap review completed',
      escalations: 1
    },
    {
      id: 11,
      category: 'Parliamentary Affairs',
      title: 'Parliamentary Questions & Petitions',
      description: 'Coordinate responses to parliamentary questions and public petitions',
      legalBasis: 'Constitution of Namibia, Parliamentary Standing Orders',
      reportingFrequency: 'Continuous',
      lastReported: '2025-01-21',
      status: 'On Track',
      compliance: 89,
      responsibleOMAs: ['All Ministries', 'National Assembly'],
      kpis: [
        { metric: 'Questions Answered (30 days)', target: 100, actual: 89, unit: '%' },
        { metric: 'Pending Responses', target: 0, actual: 12, unit: 'questions' },
        { metric: 'Petitions Resolved', target: 90, actual: 82, unit: '%' }
      ],
      recentActivity: 'January 2025 session responses submitted',
      escalations: 2
    },
    {
      id: 12,
      category: 'Disaster Management',
      title: 'National Disaster Risk Management',
      description: 'Coordinate disaster preparedness, response and recovery across sectors',
      legalBasis: 'Disaster Risk Management Act (2012)',
      reportingFrequency: 'Quarterly',
      lastReported: '2024-12-28',
      status: 'On Track',
      compliance: 84,
      responsibleOMAs: ['All Ministries', 'Disaster Risk Management Directorate'],
      kpis: [
        { metric: 'Preparedness Plans Updated', target: 23, actual: 19, unit: 'plans' },
        { metric: 'Emergency Response Time', target: 2, actual: 3, unit: 'hours' },
        { metric: 'Relief Distribution Efficiency', target: 90, actual: 84, unit: '%' }
      ],
      recentActivity: 'Drought response coordination ongoing in 5 regions',
      escalations: 0
    }
  ];

  const categories = [
    { id: 'all', label: 'All Areas', icon: FiIcons.FiGrid },
    { id: 'Policy Coordination', label: 'Policy Coordination', icon: FiIcons.FiTarget },
    { id: 'Procurement Oversight', label: 'Procurement', icon: FiIcons.FiShoppingCart },
    { id: 'Performance Management', label: 'Performance', icon: FiIcons.FiTrendingUp },
    { id: 'Financial Oversight', label: 'Financial', icon: FiIcons.FiDollarSign },
    { id: 'Service Delivery', label: 'Service Delivery', icon: FiIcons.FiUsers },
    { id: 'Audit & Compliance', label: 'Audit', icon: FiIcons.FiShield }
  ];

  const { filteredMandates, stats } = useMemo(() => {
    let filtered = mandateAreas;

    if (activeTab !== 'all') {
      filtered = filtered.filter(m => m.category === activeTab);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.title.toLowerCase().includes(query) ||
        m.description.toLowerCase().includes(query) ||
        m.legalBasis.toLowerCase().includes(query)
      );
    }

    const stats = {
      total: mandateAreas.length,
      onTrack: mandateAreas.filter(m => m.status === 'On Track').length,
      atRisk: mandateAreas.filter(m => m.status === 'At Risk').length,
      delayed: mandateAreas.filter(m => m.status === 'Delayed').length,
      avgCompliance: Math.round(
        mandateAreas.reduce((sum, m) => sum + m.compliance, 0) / mandateAreas.length
      ),
      totalEscalations: mandateAreas.reduce((sum, m) => sum + m.escalations, 0)
    };

    return { filteredMandates: filtered, stats };
  }, [activeTab, searchQuery, mandateAreas]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">OMA Mandate Monitoring</h1>
          <p className="text-slate-500">
            Constitutional and statutory responsibilities of the Office of the Prime Minister
          </p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiDownload} />
            Export Report
          </button>
          <button className="px-4 py-2 bg-grn-navy text-white rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiPlus} />
            Add Mandate Area
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Card className="text-center p-4">
          <div className="text-3xl font-black text-slate-900">{stats.total}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Mandate Areas
          </div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-green-500">
          <div className="text-3xl font-black text-green-700">{stats.onTrack}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">On Track</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-amber-500">
          <div className="text-3xl font-black text-amber-700">{stats.atRisk}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">At Risk</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-red-600">
          <div className="text-3xl font-black text-red-700">{stats.delayed}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Delayed</div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-blue-500">
          <div className="text-3xl font-black text-grn-navy">{stats.avgCompliance}%</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
            Avg Compliance
          </div>
        </Card>
        <Card className="text-center p-4 border-l-4 border-l-orange-500">
          <div className="text-3xl font-black text-orange-700">{stats.totalEscalations}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Escalations</div>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="space-y-4">
          <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
              Filter by Category
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 whitespace-nowrap',
                    activeTab === cat.id
                      ? 'bg-grn-navy text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-grn-navy/30'
                  )}
                >
                  <SafeIcon icon={cat.icon} />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="relative">
              <SafeIcon
                icon={FiIcons.FiSearch}
                className="absolute left-3 top-2.5 text-slate-400"
              />
              <input
                type="text"
                placeholder="Search mandate areas, legal basis, or KPIs..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-navy/20"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Results Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-sm font-bold text-slate-700">
          Showing {filteredMandates.length} mandate area
          {filteredMandates.length !== 1 ? 's' : ''}
        </h2>
        <select className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs bg-white">
          <option>Sort by: Status (Critical First)</option>
          <option>Sort by: Compliance (Lowest First)</option>
          <option>Sort by: Category</option>
          <option>Sort by: Last Reported</option>
        </select>
      </div>

      {/* Mandate Cards */}
      {filteredMandates.length > 0 ? (
        <div className="space-y-4">
          {filteredMandates.map(mandate => (
            <Card key={mandate.id} className="hover:shadow-lg transition-shadow">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="px-2 py-1 bg-grn-navy/10 text-grn-navy text-xs font-bold rounded uppercase tracking-wider">
                        {mandate.category}
                      </span>
                      <Badge status={mandate.status} size="sm" />
                      {mandate.escalations > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded flex items-center gap-1">
                          <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-xs" />
                          {mandate.escalations} Escalation
                          {mandate.escalations !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">{mandate.title}</h3>
                    <p className="text-sm text-slate-600 mb-2">{mandate.description}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <SafeIcon icon={FiIcons.FiBook} />
                        <span className="font-mono">{mandate.legalBasis}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <SafeIcon icon={FiIcons.FiClock} />
                        <span>Reported: {format(new Date(mandate.lastReported), 'dd MMM yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <SafeIcon icon={FiIcons.FiCalendar} />
                        <span>{mandate.reportingFrequency}</span>
                      </div>
                    </div>
                  </div>

                  {/* Compliance Score */}
                  <div className="text-center ml-6">
                    <div
                      className={clsx(
                        'text-4xl font-black mb-1',
                        mandate.compliance >= 80
                          ? 'text-green-600'
                          : mandate.compliance >= 60
                          ? 'text-amber-600'
                          : 'text-red-600'
                      )}
                    >
                      {mandate.compliance}%
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">
                      Compliance
                    </div>
                    <div className="w-24 bg-slate-200 rounded-full h-2 mt-2">
                      <div
                        className={clsx(
                          'h-2 rounded-full',
                          mandate.compliance >= 80
                            ? 'bg-green-500'
                            : mandate.compliance >= 60
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                        )}
                        style={{ width: `${mandate.compliance}%` }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* KPIs Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
                  {mandate.kpis.map((kpi, idx) => {
                    const percentage = kpi.target > 0 ? (kpi.actual / kpi.target) * 100 : 0;
                    const isGood = kpi.metric.includes('Overdue') || kpi.metric.includes('Outstanding')
                      ? kpi.actual <= kpi.target
                      : kpi.actual >= kpi.target;

                    return (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-xs font-medium text-slate-600">{kpi.metric}</p>
                          <span
                            className={clsx(
                              'text-xs font-bold',
                              isGood ? 'text-green-600' : 'text-red-600'
                            )}
                          >
                            {kpi.actual}/{kpi.target} {kpi.unit}
                          </span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-1.5">
                          <div
                            className={clsx(
                              'h-1.5 rounded-full',
                              isGood ? 'bg-green-500' : 'bg-red-500'
                            )}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Recent Activity & Responsible OMAs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      Recent Activity
                    </p>
                    <p className="text-sm text-slate-700 italic">{mandate.recentActivity}</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                      Responsible OMAs
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {mandate.responsibleOMAs.map((oma, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded border border-blue-200"
                        >
                          {oma}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-slate-100">
                  <button
                    onClick={mandate.id === 1 ? () => navigate('/cabinet-decisions') : undefined}
                    className="flex-1 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <SafeIcon icon={FiIcons.FiEye} />
                    View Full Report
                  </button>
                  <button className="flex-1 px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2">
                    <SafeIcon icon={FiIcons.FiTrendingUp} />
                    View Trends
                  </button>
                  {mandate.escalations > 0 && (
                    <button className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2">
                      <SafeIcon icon={FiIcons.FiAlertTriangle} />
                      View Escalations ({mandate.escalations})
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <div className="flex flex-col items-center justify-center text-slate-400">
            <SafeIcon icon={FiIcons.FiInbox} className="text-4xl mb-3" />
            <h3 className="text-lg font-bold text-slate-600 mb-1">No mandate areas found</h3>
            <p className="text-sm">Try adjusting your filters or search query</p>
          </div>
        </Card>
      )}

      {/* Legal Framework Reference */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <SafeIcon icon={FiIcons.FiInfo} className="text-grn-navy text-xl shrink-0 mt-0.5" />
          <div className="text-xs text-slate-700">
            <p className="font-bold mb-2">Constitutional & Legal Mandate Framework</p>
            <p className="mb-2">
              The Office of the Prime Minister derives its authority from the{' '}
              <strong>Constitution of the Republic of Namibia (1990), Article 35</strong>, which
              establishes the Prime Minister as the leader of government business and coordinator of
              Cabinet affairs.
            </p>
            <p>
              Additional statutory responsibilities are defined in: <strong>Cabinet Manual (2015)</strong>,{' '}
              <strong>Public Service Act (1995)</strong>, <strong>State Finance Act (1991)</strong>,{' '}
              <strong>Public Procurement Act (2015)</strong>, and various sector-specific legislation.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};